import { useEffect, useState } from 'react'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-toastify'
import MaterialFolderTree from './MaterialFolderTree'
import MaterialFolderFileViewer from './MaterialFolderFileViewer'
import {
  MaterialTreeNode,
  SampleProjectApi,
} from '../../services/sampleProjectApi'

interface SampleProjectViewerProps {
  courseId: number
  materialId: number
  title?: string
  admin?: boolean
}

const SampleProjectViewer = ({
  courseId,
  materialId,
  title,
  admin = false,
}: SampleProjectViewerProps) => {
  const [tree, setTree] = useState<MaterialTreeNode[]>([])
  const [selectedFile, setSelectedFile] = useState<MaterialTreeNode | null>(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        setSelectedFile(null)
        const data = await SampleProjectApi.getTree(courseId, materialId, admin)
        if (!cancelled) setTree(data.tree || [])
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Không tải được dự án mẫu')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [courseId, materialId, admin])

  const handleDownloadAll = async () => {
    try {
      setDownloading(true)
      await SampleProjectApi.downloadProject(
        courseId,
        materialId,
        title || `du-an-mau-${materialId}`,
        admin
      )
      toast.success('Đã tải dự án mẫu')
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được ZIP')
    } finally {
      setDownloading(false)
    }
  }

  const handleDownloadFolder = async (node: MaterialTreeNode) => {
    try {
      await SampleProjectApi.downloadFolder(
        courseId,
        materialId,
        node.id,
        node.name,
        admin
      )
      toast.success(`Đã tải thư mục ${node.name}`)
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được thư mục')
    }
  }

  if (loading) {
    return (
      <div className="flex h-full min-h-[240px] items-center justify-center text-m2-on-surface-variant">
        Đang tải cây thư mục...
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-full min-h-[240px] items-center justify-center text-sm text-red-600">
        {error}
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex shrink-0 items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-m2-on-surface">
            {title || 'Dự án mẫu'}
          </h3>
          <p className="text-xs text-m2-on-surface-variant">Cấu trúc thư mục dự án</p>
        </div>
        <button
          type="button"
          disabled={downloading}
          onClick={handleDownloadAll}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-m2-outline px-3 py-1.5 text-xs font-medium text-m2-on-surface hover:bg-m2-surface-high disabled:opacity-50"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          {downloading ? 'Đang tải...' : 'Tải ZIP'}
        </button>
      </div>
      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[280px_1fr]">
        <div className="min-h-0 overflow-auto rounded-lg border border-m2-outline-variant bg-m2-surface-low">
          <MaterialFolderTree
            nodes={tree}
            selectedId={selectedFile?.id}
            onSelectFile={setSelectedFile}
            onDownloadFolder={handleDownloadFolder}
          />
        </div>
        <div className="min-h-0 overflow-hidden">
          <MaterialFolderFileViewer
            courseId={courseId}
            materialId={materialId}
            node={selectedFile}
            admin={admin}
          />
        </div>
      </div>
    </div>
  )
}

export default SampleProjectViewer
